import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';
import _ from 'underscore';

import {overridePurpose} from 'archive/ArchiveActions';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as benchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {withLoadingScreen} from 'util/Promises';

import {ConnectionSchemasPageQuery} from './__generated/ConnectionSchemasPageQuery';
import {bulkArchive, bulkUnarchive, connectionSchemaURL} from './ConnectionSchemaDAO';

type ConnectionSchema = ConnectionSchemasPageQuery['connectionSchemas'][number];

interface PresentationalConnectionSchemasPageProps {
  connectionSchemas: Array<ConnectionSchema> | null;
  areOrganizationSchemasCreatable: boolean;
  refetch: () => void;
}

export class PresentationalConnectionSchemasPage extends React.Component<PresentationalConnectionSchemasPageProps> {
  bulkArchiveConnectionSchemas = async (ids: Array<string>, purpose: string): Promise<void> => {
    try {
      await withLoadingScreen(bulkArchive(ids, overridePurpose(purpose, ids)), 'Archiving...');
      notifications.success('Items successfully archived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    this.props.refetch();
  };

  bulkUnarchiveConnectionSchemas = async (ids: Array<string>): Promise<void> => {
    try {
      await bulkUnarchive(ids);
      notifications.success('Items successfully unarchived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    this.props.refetch();
  };

  override render(): JSX.Element {
    benchlingUtil.setWindowTitle(`Connection Schemas`);
    return (
      <SchemasListing
        schemas={this.props.connectionSchemas}
        schemaType={SCHEMA_TYPES.CONNECTION}
        urlForSchema={connectionSchemaURL}
        archiveSchemas={this.bulkArchiveConnectionSchemas}
        unarchiveSchemas={this.bulkUnarchiveConnectionSchemas}
        isWritable={this.props.areOrganizationSchemasCreatable}
      />
    );
  }
}

export const CONNECTION_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query ConnectionSchemasPageQuery @skipAudit @engComponent(name: INSTRUMENTS) {
    connectionSchemas {
      id
      name
      archiveRecord {
        purpose
      }
    }
  }
`;

export default function ConnectionSchemasPage(): JSX.Element {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  const queryResult = useBenchlingQuery<ConnectionSchemasPageQuery>(CONNECTION_SCHEMAS_PAGE_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const connectionSchemas =
    queryResult.type === QueryResultType.SUCCESS
      ? _.sortBy(queryResult.data.connectionSchemas, ({name}) => name.toLowerCase())
      : null;
  return (
    <PresentationalConnectionSchemasPage
      connectionSchemas={connectionSchemas}
      areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
      refetch={queryResult.refetch}
    />
  );
}
